import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import "./AppCustomProperties.css";
import { Button } from "../../../ds2/components";
import { AppCustomPropertyNew } from "./AppCustomPropertyNew";
import { CustomProperty, RoleType, EmployeeType } from "winslow-core";
import { apiGetCustomPropertiesForWorkspace } from "../../../services/EmployeeRecordService";
import { useSelector } from "react-redux";
import { selectUser } from "../../../redux/features/appData";
import toast from "react-hot-toast";
import { SETTINGS_COMPONENTS } from "./AppCustomPropertyNew";
import { SETTINGS_DISPLAY, ROLE_DISPLAY, EMPLOYEE_TYPE_DISPLAY } from "./customPropertyConfig";
import { Shield, Users, User, Check, X } from "lucide-react";
import { MyTooltip } from "../../common/MyTooltip";

// Role icon mapping
const ROLE_ICONS = {
    [RoleType.ADMIN]: <Shield size={16} className="role-icon" />,
    [RoleType.MANAGER]: <Users size={16} className="role-icon" />,
    [RoleType.EMPLOYEE]: <User size={16} className="role-icon" />,
};

type AppCustomPropertiesProps = {

}   

export const AppCustomProperties = ({} : AppCustomPropertiesProps) => {

    const storeUser = useSelector(selectUser);
    
    const { customPropertyId } = useParams();   // id or "new"
    const navigate = useNavigate();

    const [customProperties, setCustomProperties] = useState<CustomProperty[] | undefined>(undefined);
    const [searchTerm, setSearchTerm] = useState<string>("");
    const [selectedEmployeeType, setSelectedEmployeeType] = useState<string>("All");

    useEffect(() => {
        // if path is /app/custom-properties/"", then fetch custom properties
        if (!customProperties || customPropertyId === "refresh") {
            (async () => {
                // alert("fetching custom properties");
                const cps = await apiGetCustomPropertiesForWorkspace(storeUser?.currentWorkspaceId || "");

                if (!cps) {
                    toast("Error loading custom properties");
                    return;
                }

                setCustomProperties(cps);
            })();
        }
    },[customPropertyId]);

    // Filter and sort custom properties based on search term and employee type
    const filteredCustomProperties = customProperties?.filter((cp) => {
        // Search term filter
        const matchesSearch = !searchTerm || 
            cp.display.toLowerCase().includes(searchTerm.toLowerCase()) ||
            cp.label.toLowerCase().includes(searchTerm.toLowerCase());
        
        // Employee type filter
        const matchesEmployeeType = selectedEmployeeType === "All" || 
            cp.visibleEmployeeTypes.includes(selectedEmployeeType as EmployeeType);
        
        return matchesSearch && matchesEmployeeType;
    }).sort((a, b) => {
        return a.display.toLowerCase().localeCompare(b.display.toLowerCase());
    }) || [];

    return (
        <div className="app-custom-properties">
            {customPropertyId === "new" &&
                <AppCustomPropertyNew />
            }
            {customPropertyId !== "new" && customProperties &&
                <>
                    <div className="app-custom-properties-contents">
                        <div className="app-custom-properties-actions">
                            <div className="app-custom-properties-search">
                                <input
                                    type="text"
                                    placeholder="Search by name or label..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="app-custom-properties-search-input"
                                />
                            </div>
                            <div className="app-custom-properties-filter">
                                <select
                                    value={selectedEmployeeType}
                                    onChange={(e) => setSelectedEmployeeType(e.target.value)}
                                    className="app-custom-properties-employee-type-filter"
                                >
                                    <option value="All">All Employee Types</option>
                                    {Object.keys(EmployeeType).map((employeeType) => (
                                        <option key={employeeType} value={employeeType}>
                                            {EMPLOYEE_TYPE_DISPLAY[employeeType as EmployeeType]}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <Button
                                variant="primary"
                                onClick={() => {
                                    navigate("/app/custom-properties/new");
                                }}>
                                Add
                            </Button>
                            
                            {/* <button
                                className="button-save"
                                onClick={() => {
                                    navigate("/app/custom-properties/new");
                                }}
                            >
                                Add
                            </button> */}
                        </div>
                        <div className="app-custom-properties-grid-container">
                            <div className="app-custom-properties-grid-header">
                                <div>Name</div>
                                <div className="app-custom-properties-grid-header-center">Type</div>
                                <div className="app-custom-properties-grid-header-center">Required</div>
                                <div className="app-custom-properties-grid-header-center">Hidden</div>
                                <div>Roles</div>
                                <div>Description</div>
                                <div>Label</div>
                            </div>
                            {filteredCustomProperties.map((cp) => {
                                return (
                                    <div 
                                        key={cp.id}
                                        className="app-custom-properties-grid-row"
                                    >
                                        <div className="app-custom-properties-grid-cell">
                                            <div 
                                                className="app-custom-properties-grid-display"
                                                onClick={()=> {
                                                    navigate(`/app/custom-properties/update/${cp.id}`);
                                                }}
                                            >
                                                {cp.display}
                                            </div>
                                        </div>
                                        <div className="app-custom-properties-grid-cell app-custom-properties-grid-cell-center">
                                            <MyTooltip title={SETTINGS_DISPLAY[cp.type]}>
                                                <div>
                                                    {SETTINGS_COMPONENTS[cp.type]}
                                                </div>
                                            </MyTooltip>
                                        </div>
                                        <div className="app-custom-properties-grid-cell app-custom-properties-grid-cell-center">
                                            {cp.required ? (
                                                <Check size={16} className="required-check-icon" />
                                            ) : null}
                                        </div>
                                        <div className="app-custom-properties-grid-cell app-custom-properties-grid-cell-center">
                                            {cp.hidden ? (
                                                <X size={16} className="hidden-x-icon" />
                                            ) : null}
                                        </div>
                                        <div className="app-custom-properties-grid-cell">
                                            <div className="role-icons-container">
                                                {cp.visibleRoles.map((role, index) => {
                                                    return (
                                                        <MyTooltip key={role} title={ROLE_DISPLAY[role]}>
                                                            <span className="role-icon-wrapper">
                                                                {ROLE_ICONS[role]}
                                                            </span>
                                                        </MyTooltip>
                                                    )
                                                })}
                                            </div>
                                        </div>

                                        <div className="app-custom-properties-grid-cell">
                                            {cp.description}
                                        </div>
                                        <div className="app-custom-properties-grid-cell">
                                            <div className="app-custom-properties-label">
                                                {cp.label}
                                            </div>
                                        </div>
                                    </div>
                                )
                            })}
                        </div>

                        <div style={{paddingBottom: "24px"}}>{ " "}</div>
                    </div>
                </>
            }
        </div>
    )
}